/**
 * Runtime shim that implements the styled-system prop API without styled-system
 * or styled-components. Given a component's props, it returns the utility
 * `className` + inline `style` that reproduce the requested styling, plus the
 * `rest` of the props with every consumed system prop removed.
 *
 * Preferred path is a Tailwind-named utility class (backed by the shipped
 * `system-props-utilities.css`); inline styles are used only for values that
 * have no class equivalent (raw CSS values, un-tokenized colors).
 */

import { abbreviate } from "@sproutsocial/seeds-react-theme/token-utils";
import {
  COLOR_PROPS,
  ENUM_PROPS,
  FONT_SIZE_SCALE_KEYS,
  LAYOUT_PROPS,
  SCALE_PROPS,
  SEMANTIC_COLOR_TOKENS,
  SPACE_PROPS,
  SPACE_SCALE_KEYS,
  SYSTEM_PROP_NAMES,
  TYPE_SCALE_PROP_NAME,
} from "./scales";

type StyleObject = Record<string, string | number>;

export interface ResolvedStyledProps<R = Record<string, unknown>> {
  className: string;
  style: StyleObject;
  rest: R;
}

const SPACE_KEY_SET = new Set<string>(SPACE_SCALE_KEYS);
const FONT_SIZE_KEY_SET = new Set<string>(FONT_SIZE_SCALE_KEYS);

/** Split a value into its sign and absolute scale-key candidate. */
function splitSign(value: string | number): { negative: boolean; abs: string } {
  const str = String(value);
  if (str.startsWith("-")) return { negative: true, abs: str.slice(1) };
  return { negative: false, abs: str };
}

function resolveSpaceProp(
  prop: (typeof SPACE_PROPS)[string],
  value: string | number
): { className?: string; style?: StyleObject } {
  const { negative, abs } = splitSign(value);

  if (!negative && prop.keywords?.[abs]) {
    return { className: `${prop.prefix}-${prop.keywords[abs]}` };
  }

  if (SPACE_KEY_SET.has(abs)) {
    const className = `${negative ? "-" : ""}${prop.prefix}-${abs}`;
    return { className };
  }

  // Raw / non-scale value → inline style.
  const style: StyleObject = {};
  if ("axis" in prop) {
    style[prop.axis[0]] = value;
    style[prop.axis[1]] = value;
  } else {
    style[prop.css] = value;
  }
  return { style };
}

function resolveScaleProp(
  prop: (typeof SCALE_PROPS)[string],
  value: string | number
): { className?: string; style?: StyleObject } {
  const { negative, abs } = splitSign(value);
  if (!negative && prop.keys.includes(abs)) {
    return { className: `${prop.prefix}-${abs}` };
  }
  return { style: { [prop.css]: value } };
}

function resolveEnumProp(
  prop: (typeof ENUM_PROPS)[string],
  value: string | number
): { className?: string; style?: StyleObject } {
  const className = prop.map[String(value)];
  if (className) return { className };
  return { style: { [prop.css]: value } };
}

function resolveColorProp(
  prop: (typeof COLOR_PROPS)[string],
  value: string | number
): { className?: string; style?: StyleObject } {
  const str = String(value);

  if (prop.keywords?.[str]) {
    return { className: `${prop.prefix}-${prop.keywords[str]}` };
  }

  const token = SEMANTIC_COLOR_TOKENS[str];
  if (token) return { className: `${prop.prefix}-${token}` };

  // No mapped utility → inline. Raw colors (hex/keyword) pass through as-is;
  // a dotted token path becomes a var() reference. Each segment goes through the
  // same `abbreviate` the CSS var generator uses (background→bg, foreground→fg),
  // so the name matches the shipped `--color-*` var (e.g.
  // growth.opportunity.background.base → var(--color-growth-opportunity-bg-base)).
  const inlineValue = str.includes(".")
    ? `var(--color-${str.split(".").map(abbreviate).join("-")})`
    : value;
  return { style: { [prop.css]: inlineValue } };
}

/**
 * `typeScale` is a styled-system `variant` (theme.typography lookup), not a
 * plain CSS property — there's no legitimate raw-value fallback (an
 * unrecognized key means styled-system's variant emits nothing). It reuses
 * the same `text-{key}` class `fontSize` emits.
 */
function resolveTypeScaleProp(value: string | number): { className?: string } {
  const key = String(value);
  return FONT_SIZE_KEY_SET.has(key) ? { className: `text-${key}` } : {};
}

function resolveLayoutProp(
  prop: (typeof LAYOUT_PROPS)[string],
  value: string | number
): { className?: string; style?: StyleObject } {
  if (typeof value === "string" && prop.keywords?.[value]) {
    return { className: `${prop.classPrefix}-${prop.keywords[value]}` };
  }
  // styled-system: a number in (0, 1] on `width` is a percentage.
  const resolved =
    prop.fraction && typeof value === "number" && value > 0 && value <= 1
      ? `${value * 100}%`
      : value;
  return { style: { [prop.css]: resolved } };
}

export function resolveStyledProps<P extends Record<string, unknown>>(
  props: P
): ResolvedStyledProps<Partial<P>> {
  const classes: string[] = [];
  const style: StyleObject = {};
  const rest: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(props)) {
    const v = value as string | number;

    // Each recognized system prop is consumed (removed from rest). A null/
    // undefined value emits nothing but is still consumed — styled-system
    // ignores them.
    let resolved: { className?: string; style?: StyleObject } | undefined;
    if (SPACE_PROPS[key]) {
      resolved = value != null ? resolveSpaceProp(SPACE_PROPS[key], v) : {};
    } else if (SCALE_PROPS[key]) {
      resolved = value != null ? resolveScaleProp(SCALE_PROPS[key], v) : {};
    } else if (ENUM_PROPS[key]) {
      resolved = value != null ? resolveEnumProp(ENUM_PROPS[key], v) : {};
    } else if (COLOR_PROPS[key]) {
      resolved = value != null ? resolveColorProp(COLOR_PROPS[key], v) : {};
    } else if (LAYOUT_PROPS[key]) {
      resolved = value != null ? resolveLayoutProp(LAYOUT_PROPS[key], v) : {};
    } else if (key === TYPE_SCALE_PROP_NAME) {
      resolved = value != null ? resolveTypeScaleProp(v) : {};
    } else if (SYSTEM_PROP_NAMES.has(key)) {
      // Recognized styled-system prop with no class mapping yet → inline it so
      // it never leaks onto the DOM as an invalid attribute.
      resolved = value != null ? { style: { [key]: v } } : {};
    }

    if (resolved) {
      if (resolved.className) classes.push(resolved.className);
      if (resolved.style) Object.assign(style, resolved.style);
      continue;
    }

    rest[key] = value;
  }

  return {
    className: classes.join(" "),
    style,
    rest: rest as Partial<P>,
  };
}
